/* Merchant opening hours in El Paso local time. Shared holidays come from the delivery calendar. */
(function (root, factory) {
  "use strict";
  if (typeof module === "object" && module.exports) {
    module.exports = factory(require("./saber-delivery-calendar.js"));
  } else {
    root.SaberStoreStatus = factory(root.SaberLocalDeliveryCalendar);
    root.SaberStoreStatus.mount(root);
  }
})(typeof globalThis !== "undefined" ? globalThis : this, function (calendar) {
  "use strict";
  const timezone = "America/Denver";
  const openingMinute = 8 * 60 + 30;
  const closingMinute = 17 * 60 + 30;
  const clockLabel = (minute, spanish) => `${Math.floor(minute / 60) % 12 || 12}:${String(minute % 60).padStart(2, "0")} ${minute < 12 * 60 ? spanish ? "a. m." : "a.m." : spanish ? "p. m." : "p.m."}`;
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const unknown = () => ({ state: "unknown", label: "", nextOpening: null, nextOpeningLabel: "" });
  let formatter;

  function getStoreStatus({ now, policy, dateFormatter } = {}) {
    const timestamp = now === undefined ? Date.now() : now instanceof Date ? now.valueOf() : now;
    if (typeof timestamp !== "number" || !Number.isFinite(timestamp) || !calendar?.isOpenDay) return unknown();
    const closures = policy?.additionalClosures === undefined ? [] : policy.additionalClosures;
    if (!Array.isArray(closures)) return unknown();
    try {
      const clockFormatter = dateFormatter || (formatter ||= new Intl.DateTimeFormat("en-US", {
        timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", hourCycle: "h23",
      }));
      const clock = Object.fromEntries(clockFormatter.formatToParts(new Date(timestamp)).map(part => [part.type, part.value]));
      const day = `${clock.year}-${clock.month}-${clock.day}`;
      const todayOpen = calendar.isOpenDay(day, closures);
      const minute = Number(clock.hour) * 60 + Number(clock.minute);
      if (todayOpen === null || !Number.isFinite(minute) || minute < 0 || minute >= 1440) return unknown();
      if (todayOpen && minute >= openingMinute && minute < closingMinute) {
        return { state: "open", label: "Open", nextOpening: null, nextOpeningLabel: "" };
      }
      const next = new Date(day + "T00:00:00Z");
      // Civil-date arithmetic is independent of daylight-saving transitions.
      for (let offset = 0; offset < 800; offset++) {
        if (offset) next.setUTCDate(next.getUTCDate() + 1);
        const nextDay = next.toISOString().slice(0, 10);
        const open = calendar.isOpenDay(nextDay, closures);
        if (open === null) return unknown();
        if (!open || offset === 0 && minute >= openingMinute) continue;
        const weekday = days[next.getUTCDay()];
        const when = offset === 0 ? "today" : offset === 1 ? "tomorrow" : offset < 7 ? weekday : `${weekday}, ${months[next.getUTCMonth()]} ${next.getUTCDate()}`;
        return {
          state: "closed", label: "Closed", nextOpening: nextDay,
          nextOpeningLabel: `Opens ${when} at 8:30 a.m.`,
        };
      }
      return unknown();
    } catch (_) { return unknown(); }
  }

  function mount(win) {
    if (!win?.document || !win.customElements || win.customElements.get("saber-store-status")) return;
    const elements = new Set();
    let timer;
    function apply(element, result) {
      const label = element.querySelector("[data-store-state]");
      const next = element.querySelector("[data-store-next]");
      if (!label || !next) return;
      if (result.state === "unknown") { element.hidden = true; return; }
      const spanish = element.dataset.locale === "es";
      const stateLabel = result.state === "open" ? (element.dataset.openLabel || result.label) : (element.dataset.closedLabel || result.label);
      let nextLabel = result.state === "open" ? `${spanish ? "Cierra a las" : "Closes at"} ${clockLabel(closingMinute, spanish)}` : result.nextOpeningLabel;
      if (spanish && result.nextOpening) {
        try {
          const date = new win.Intl.DateTimeFormat("es-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" }).format(new win.Date(result.nextOpening + "T12:00:00Z"));
          nextLabel = "Abre " + date + " a las 8:30 a. m.";
        } catch (_) { /* Original opening information remains available. */ }
      }
      if (label.textContent !== stateLabel) label.textContent = stateLabel;
      if (next.textContent !== nextLabel) next.textContent = nextLabel;
      next.hidden = !nextLabel;
      element.dataset.state = result.state;
      element.setAttribute("aria-label", spanish ? `Saber Sales: ${stateLabel.toLowerCase()}${nextLabel ? ". " + nextLabel : ""}` : `Saber Sales is ${result.label.toLowerCase()}${nextLabel ? ". " + nextLabel : ""}`);
      element.hidden = false;
    }
    function refresh() {
      const result = getStoreStatus({ now: win.Date.now(), policy: win.SaberLocalDeliveryPolicy });
      elements.forEach(element => apply(element, result));
    }
    function schedule() {
      win.clearTimeout(timer);
      if (!elements.size) return;
      timer = win.setTimeout(() => { refresh(); schedule(); }, 60000 - win.Date.now() % 60000 + 25);
    }
    class StoreStatus extends win.HTMLElement {
      connectedCallback() {
        elements.add(this);
        apply(this, getStoreStatus({ now: win.Date.now(), policy: win.SaberLocalDeliveryPolicy }));
        if (elements.size === 1) schedule();
      }
      disconnectedCallback() {
        elements.delete(this);
        if (!elements.size) win.clearTimeout(timer);
      }
    }
    win.customElements.define("saber-store-status", StoreStatus);
    win.document.addEventListener("visibilitychange", () => {
      if (!win.document.hidden) { refresh(); schedule(); }
    });
    win.addEventListener("pageshow", () => { refresh(); schedule(); });
  }
  return Object.freeze({ getStoreStatus, mount });
});
